/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    Category,
    Configuration,
    ConfigurationParameters,
    FilteredGuideApi,
    GuideProgramList,
    GuideProgramsGetRequest,
} from 'client-code/filtered-guide';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IFilteredGuideServiceRepository } from '../interfaces/IFilteredGuideServiceRepository';
import { Log } from '../logging';
import { TServicesParams } from '../types';

export class FilteredGuideRepositoryImpl implements IFilteredGuideServiceRepository {
    private readonly TAG: string = 'FilteredGuideRepositoryImpl';

    private mCommonHeaders: CommonHeaders;

    private mFilteredGuideApi: FilteredGuideApi;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
        this.mCommonHeaders = new CommonHeaders('FilteredGuide');

        const oConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };

        const configuration = new Configuration(oConfigParams);
        this.mFilteredGuideApi = new FilteredGuideApi(configuration);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZone,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * Fetches a list of filtered programs based on the provided category, start time,
     *  and optional channel filter.
     * @param category - The category of programs to filter by.
     * @param startTime - The start time for filtering programs.
     * @param channelFilter - (Optional) Filter for channels, either 'favorites' or 'subscribed'.
     * @param offset - (Optional) The offset for pagination. Defaults to 0.
     * @param count - (Optional) The maximum number of programs to fetch. Defaults to 1000.
     * @returns A promise resolving to the filtered list of programs.
     * @throws Will throw an error if the fetch operation fails.
     */
    public async fetchFilteredPrograms(
        category: Category,
        startTime: Date,
        channelFilter?: 'favorites' | 'subscribed',
        offset: number = 0,
        count: number = 1000,
    ): Promise<GuideProgramList> {
        try {
            const startTimeInSeconds: number = Math.floor(new Date(startTime).getTime() / 1000);
            const commonHeaders = this.getCommonRequestParams();

            const requestParameters: GuideProgramsGetRequest = {
                category,
                ...commonHeaders,
                ...(channelFilter && { channelFilter }),
                offset,
                count,
                msoPartnerId: this.mServiceParams.msoPartnerId,
                startTime: startTimeInSeconds,
                whiny: this.mServiceParams.whiny || 'false',
                xRequestToken: this.mServiceParams.requestTokenHeader || undefined,
            };

            const response = await this.mFilteredGuideApi.guideProgramsGet(requestParameters);
            return response.data;
        } catch (error: any) {
            Log.e(this.TAG, 'fetchFilteredPrograms()', error.message, {
                channelFilter,
                category,
                startTime,
            });
            throw error;
        }
    }
}
